import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useParams } from "react-router-dom";
import { brl, dt, methodLabel } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Landmark, Printer, CheckCircle2, ExternalLink, Copy, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";

export default function Receipt() {
  const { token } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/receipts/${token}`);
        setReceipt(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Recibo não encontrado");
      }
    })();
  }, [token]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiado");
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const downloadImage = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `recibo-${(receipt?.receipt_token || "").slice(0, 10)}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Imagem do recibo salva");
      }, "image/png");
    } catch (e) {
      toast.error("Falha ao gerar imagem");
    } finally {
      setDownloading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-card p-8 text-center max-w-sm" data-testid="receipt-error">
          <div className="text-white font-bold text-lg mb-2">Recibo indisponível</div>
          <div className="text-slate-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }
  if (!receipt) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-slate-400">
        Carregando recibo...
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-10 px-4 flex justify-center print:bg-white print:py-0"
      data-testid="receipt-page"
    >
      <div className="w-full max-w-2xl">
        {/* Actions - hidden on print */}
        <div className="flex flex-wrap justify-end gap-2 mb-4 print:hidden">
          <Button
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            onClick={copyLink}
            data-testid="receipt-copy-link"
          >
            <Copy className="w-4 h-4 mr-2" /> Copiar link
          </Button>
          <Button
            variant="outline"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
            onClick={downloadImage}
            disabled={downloading}
            data-testid="receipt-download-image"
          >
            <ImageIcon className="w-4 h-4 mr-2" /> Baixar imagem
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-500 text-white"
            onClick={() => window.print()}
            data-testid="receipt-print-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Baixar PDF
          </Button>
        </div>

        <div
          ref={cardRef}
          className="bg-white text-slate-900 rounded-2xl shadow-2xl p-6 sm:p-8 md:p-10 print:shadow-none print:rounded-none"
          style={{ fontFamily: "Manrope, system-ui, sans-serif" }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-6 mb-6">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shrink-0">
                <Landmark className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <div
                  className="text-lg font-extrabold truncate"
                  style={{ fontFamily: "Outfit", letterSpacing: "-0.03em" }}
                >
                  {receipt.business_name}
                </div>
                <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold">
                  Recibo de Pagamento
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-slate-500 uppercase font-semibold">Nº do recibo</div>
              <div className="text-xs font-mono text-slate-700 font-bold">
                {receipt.receipt_token?.slice(0, 10).toUpperCase()}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-6 min-w-0">
            <CheckCircle2 className="w-9 h-9 text-emerald-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-500 font-semibold">Confirmação de pagamento</div>
              <div
                className="font-extrabold tracking-tight break-words"
                style={{
                  fontFamily: "Outfit",
                  fontSize: "clamp(1.5rem, 6vw, 2.25rem)",
                  letterSpacing: "-0.03em",
                  fontVariantNumeric: "tabular-nums",
                }}
                data-testid="receipt-amount"
              >
                {brl(receipt.paid_amount)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Cliente
              </div>
              <div className="font-bold break-words">{receipt.client_name}</div>
              {receipt.client_phone && (
                <div className="text-slate-500 text-xs">{receipt.client_phone}</div>
              )}
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Data
              </div>
              <div className="font-bold">{dt(receipt.paid_at)}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Forma de pagamento
              </div>
              <div className="font-bold">{methodLabel[receipt.payment_method] || "-"}</div>
            </div>
            <div>
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Parcela
              </div>
              <div className="font-bold">
                {receipt.installment_number} de {receipt.installments_count}
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                Contrato base
              </div>
              <div className="font-bold break-words">
                Empréstimo de{" "}
                <span
                  className="font-mono font-extrabold"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {brl(receipt.loan_amount)}
                </span>{" "}
                ·{" "}
                {receipt.collection_method === "a_vista" ? "À Vista" : "Parcelado"}
              </div>
            </div>
            {receipt.note && (
              <div className="sm:col-span-2">
                <div className="text-[11px] text-slate-500 uppercase tracking-widest font-semibold">
                  Observação
                </div>
                <div className="italic text-slate-700 break-words">{receipt.note}</div>
              </div>
            )}
          </div>

          <div className="mt-10 pt-6 border-t border-slate-200 text-center text-xs text-slate-500 font-medium">
            Documento gerado eletronicamente por {receipt.business_name}. <br />
            <span className="inline-flex items-center gap-1 mt-1 break-all">
              <ExternalLink className="w-3 h-3 shrink-0" /> {window.location.href}
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body { background: white !important; }
          @page { size: A4; margin: 15mm; }
        }
      `}</style>
    </div>
  );
}
